/**
 * Klaviyo Adapter
 *
 * Adapts standardized event payloads into Klaviyo events.
 *
 * Metric names follow Klaviyo's own vocabulary: the names its WooCommerce
 * integration produces where they exist (Viewed Product, Added to Cart,
 * Started Checkout, Placed Order, Ordered Product), the names its Shopify
 * integration produces for the rest (Viewed Collection, Submitted Search),
 * and the same Title Case past tense for events Klaviyo has no name for.
 * Property names are the PascalCase ones Klaviyo's integrations use, so flow
 * templates written for them keep rendering.
 *
 * Which events are sent depends on the coexistence mode with the official
 * Klaviyo plugin (pmwDataLayer.pixels.klaviyo.mode):
 *
 * - takeover and standalone: every event below
 * - gap_fill: only the events the Klaviyo plugin never sends itself, so
 *   Viewed Product, Added to Cart and Started Checkout return null
 *
 * Purchases are reported server-side (in standalone mode through the Events
 * API, otherwise by Klaviyo's own order sync). The browser only sends them in
 * standalone mode without the Events API, see adaptPurchase().
 *
 * Every event goes out through pmw.klaviyoTrack(), which tags the metric with
 * service "woocommerce" so it lands on the same Klaviyo metric the plugin's
 * events would have.
 *
 * @see https://developers.klaviyo.com/en/docs/javascript_api
 * @see https://help.klaviyo.com/hc/en-us/articles/360030732832
 */

(function (pmw, undefined) {

	class KlaviyoAdapter {

		/**
		 * Adapt an event for Klaviyo
		 *
		 * @param {Object} eventData - Standardized event data
		 * @param {string} eventName - Standardized event name
		 * @param {Object} context - Additional context
		 * @returns {Object|null} - Klaviyo formatted event or null to skip
		 */
		adapt(eventData, eventName, context) {
			const methodName = `adapt${this.toPascalCase(eventName)}`;

			if (typeof this[methodName] === "function") {
				return this[methodName](eventData, context);
			}

			// Klaviyo does not get every standardized event - skip the rest.
			return null;
		}

		toPascalCase(str) {
			if (typeof str !== "string") return "";
			return str.replace(/(^|_)(\w)/g, (_, __, c) => c.toUpperCase());
		}

		settings() {
			return pmwDataLayer?.pixels?.klaviyo || {};
		}

		/**
		 * In gap-fill mode the Klaviyo plugin still sends Viewed Product, Added
		 * to Cart and Started Checkout itself, so sending them here would
		 * double every one of them in Klaviyo.
		 */
		isGapFill() {
			return "gap_fill" === this.settings().mode;
		}

		/**
		 * Round a value to two decimals.
		 */
		toPrice(value) {
			const price = Math.round(Number(value) * 100) / 100;
			return Number.isFinite(price) ? price : 0;
		}

		currency() {
			return pmwDataLayer?.shop?.currency;
		}

		/**
		 * The category names of a product as an array.
		 */
		getCategories(product) {
			if (!product?.category) return [];
			return Array.isArray(product.category) ? product.category.map(String) : [ String(product.category) ];
		}

		/**
		 * One line item in the property shape of Klaviyo's integrations.
		 *
		 * The Pixel Manager's product object carries the variation as the
		 * product when variations are output, so ProductID is the ID the shop
		 * tracks with and VariantID is set on variations.
		 */
		getItem(product, quantity) {
			if (!product) return null;

			const qty   = Number(quantity ?? product.quantity) || 1;
			const price = this.toPrice(product.price);

			const item = {
				ProductID  : product.id ? String(product.id) : undefined,
				ProductName: product.name,
				Quantity   : qty,
				ItemPrice  : price,
				RowTotal   : this.toPrice(price * qty),
				Categories : this.getCategories(product),
			};

			// The product objects the events carry keep the SKU under dyn_r_ids.
			const sku = product.sku || product.dyn_r_ids?.sku;

			if (product.is_variation) item.VariantID = String(product.id);
			if (sku) item.SKU = String(sku);
			if (product.brand) item.Brand = product.brand;

			return item;
		}

		/**
		 * The tracked cart as Klaviyo cart properties, or null when it is empty.
		 */
		getCartProperties() {
			if (!pmwDataLayer?.cart || pmw.isEmptyObject(pmwDataLayer.cart)) return null;

			const items = [];

			Object.values(pmwDataLayer.cart).forEach(product => {
				const item = this.getItem(product, product.quantity);
				if (item) items.push(item);
			});

			if (!items.length) return null;

			const categories = [];
			items.forEach(item => item.Categories.forEach(category => {
				if (!categories.includes(category)) categories.push(category);
			}));

			return {
				$value    : this.toPrice(pmw.getCartValue()),
				Currency  : this.currency(),
				ItemNames : items.map(item => item.ProductName),
				Categories: categories,
				ItemCount : items.reduce((total, item) => total + item.Quantity, 0),
				Items     : items,
			};
		}

		/**
		 * Adapt ViewItemList event
		 *
		 * The Pixel Manager emits one view_item_list event per visible product.
		 * The event listener collects them and sends a single "Viewed
		 * Collection" per page, so this returns the one item it was given.
		 */
		adaptViewItemList(eventData, context) {
			const item = this.getItem(eventData.product, 1);
			if (!item) return null;

			return {
				event_name: "Viewed Collection",
				properties: {
					CollectionName: eventData.listName || eventData.list_name || pmwDataLayer?.shop?.list_name,
					Items         : [ item ],
				},
			};
		}

		/**
		 * Adapt SelectItem event
		 */
		adaptSelectItem(eventData, context) {
			const item = this.getItem(eventData.product, 1);
			if (!item) return null;

			item.CollectionName = eventData.listName || eventData.list_name || pmwDataLayer?.shop?.list_name;

			return {
				event_name: "Selected Product",
				properties: item,
			};
		}

		/**
		 * Adapt ViewItem event
		 *
		 * Returns the Viewed Product event plus the item for klaviyo.js's
		 * trackViewedItem(), which feeds Klaviyo's recently viewed store that
		 * browse abandonment flows and product blocks read from.
		 */
		adaptViewItem(eventData, context) {
			if (this.isGapFill()) return null;

			const item = this.getItem(eventData.product, 1);
			if (!item) return null;

			return {
				event_name : "Viewed Product",
				properties : Object.assign({$value: item.ItemPrice, Currency: this.currency()}, item),
				viewed_item: {
					Title     : item.ProductName,
					ItemId    : item.ProductID,
					Categories: item.Categories,
					Metadata  : {
						Price: item.ItemPrice,
					},
				},
			};
		}

		/**
		 * Adapt AddToCart event
		 *
		 * The added item is described with the AddedItem prefix the Klaviyo
		 * plugin uses, with the whole cart alongside.
		 */
		adaptAddToCart(eventData, context) {
			if (this.isGapFill()) return null;

			const product  = eventData.product;
			const quantity = Number(product?.quantity) || 1;
			const item     = this.getItem(product, quantity);
			if (!item) return null;

			const cart = this.getCartProperties() || {};

			return {
				event_name: "Added to Cart",
				properties: Object.assign({}, cart, {
					$value               : cart.$value ?? item.RowTotal,
					Currency             : this.currency(),
					AddedItemProductID   : item.ProductID,
					AddedItemProductName : item.ProductName,
					AddedItemQuantity    : item.Quantity,
					AddedItemPrice       : item.ItemPrice,
					AddedItemSKU         : item.SKU,
					AddedItemCategories  : item.Categories,
				}),
			};
		}

		/**
		 * Adapt RemoveFromCart event
		 */
		adaptRemoveFromCart(eventData, context) {
			const product  = eventData.product;
			const quantity = Number(product?.quantity) || 1;
			const item     = this.getItem(product, quantity);
			if (!item) return null;

			const cart = this.getCartProperties() || {};

			return {
				event_name: "Removed from Cart",
				properties: Object.assign({}, cart, {
					Currency              : this.currency(),
					RemovedItemProductID  : item.ProductID,
					RemovedItemProductName: item.ProductName,
					RemovedItemQuantity   : item.Quantity,
					RemovedItemPrice      : item.ItemPrice,
					RemovedItemSKU        : item.SKU,
					RemovedItemCategories : item.Categories,
				}),
			};
		}

		/**
		 * Adapt ViewCart event
		 */
		adaptViewCart(eventData, context) {
			const cart = this.getCartProperties();
			if (!cart) return null;

			return {
				event_name: "Viewed Cart",
				properties: cart,
			};
		}

		/**
		 * Adapt BeginCheckout event
		 *
		 * In takeover mode the server hands over the Started Checkout event
		 * data the Klaviyo plugin itself would have sent, built by the plugin's
		 * wck_build_cart_data(): items with URLs and images, totals, and the
		 * CartRebuildKey that abandoned cart emails link back with. That keeps
		 * every flow template a merchant built for the plugin rendering. In
		 * standalone mode the tracked cart is used instead.
		 */
		adaptBeginCheckout(eventData, context) {
			if (this.isGapFill()) return null;

			const pluginData = this.settings().started_checkout;

			if (pluginData && typeof pluginData === "object") {
				return {
					event_name: "Started Checkout",
					properties: Object.assign({}, pluginData, {
						CheckoutURL: window.location.href,
					}),
				};
			}

			const cart = this.getCartProperties();

			return {
				event_name: "Started Checkout",
				properties: Object.assign({}, cart || {}, {
					CheckoutURL: window.location.href,
					$extra     : {
						Items: cart?.Items || [],
					},
				}),
			};
		}

		/**
		 * Adapt AddShippingInfo event
		 */
		adaptAddShippingInfo(eventData, context) {
			return {
				event_name: "Added Shipping Info",
				properties: this.getCartProperties() || {},
			};
		}

		/**
		 * Adapt AddPaymentInfo event
		 */
		adaptAddPaymentInfo(eventData, context) {
			return {
				event_name: "Added Payment Info",
				properties: this.getCartProperties() || {},
			};
		}

		/**
		 * Adapt AddToWishlist event
		 */
		adaptAddToWishlist(eventData, context) {
			const item = this.getItem(eventData.product, 1);
			if (!item) return null;

			return {
				event_name: "Added to Wishlist",
				properties: Object.assign({$value: item.ItemPrice, Currency: this.currency()}, item),
			};
		}

		/**
		 * Adapt Search event
		 */
		adaptSearch(eventData, context) {
			return {
				event_name: "Submitted Search",
				properties: {
					SearchTerm: pmw.getSearchTermFromUrl(),
				},
			};
		}

		/**
		 * Adapt Purchase event
		 *
		 * The browser only reports purchases when the Pixel Manager runs
		 * standalone and the Events API is not configured. With the Events API
		 * the server sends them from the order hooks, with the final totals and
		 * refunds. With the Klaviyo plugin installed, Klaviyo produces Placed
		 * Order from its own order sync, and Klaviyo does not deduplicate our
		 * unique_id against that event, so anything sent here would double the
		 * order.
		 *
		 * Returns the Placed Order event with one Ordered Product event per
		 * line item attached, the pair Klaviyo's integrations produce. The
		 * unique IDs derive from the order ID, so reloading the confirmation
		 * page can never count the order twice.
		 */
		adaptPurchase(eventData, context) {
			const settings = this.settings();

			if ("standalone" !== settings.mode || settings.events_api) return null;

			const order = pmwDataLayer?.order;
			if (!order) return null;

			const items = [];

			Object.values(order.items || {}).forEach(orderItem => {

				let product = pmwDataLayer.products?.[orderItem.id];

				if (pmwDataLayer?.shop?.variations_output && orderItem.variation_id && 0 !== orderItem.variation_id) {
					product = pmwDataLayer.products?.[orderItem.variation_id] || product;
				}

				const item = this.getItem(product || orderItem, Number(orderItem.quantity) || 1);
				if (!item) return;

				// The order item carries the actually paid price and name.
				item.ItemPrice = this.toPrice(orderItem.price);
				item.RowTotal  = this.toPrice(orderItem.price * item.Quantity);
				if (orderItem.name) item.ProductName = orderItem.name;
				item.ItemId = String(orderItem.id) + (orderItem.variation_id ? "_" + String(orderItem.variation_id) : "");

				items.push(item);
			});

			const value    = this.toPrice(order.value?.marketing);
			const orderId  = String(order.id);
			const currency = order.currency;

			const categories = [];
			items.forEach(item => item.Categories.forEach(category => {
				if (!categories.includes(category)) categories.push(category);
			}));

			return {
				event_name      : "Placed Order",
				unique_id       : "pmw_" + orderId,
				value           : value,
				properties      : {
					$event_id    : orderId,
					$value       : value,
					OrderId      : String(order.number || order.id),
					Currency     : currency,
					Categories   : categories,
					ItemNames    : items.map(item => item.ProductName),
					ItemCount    : items.reduce((total, item) => total + item.Quantity, 0),
					Items        : items,
					DiscountCode : order.coupon || "",
					DiscountValue: this.toPrice(order.discount),
					$extra       : {
						ShippingTotal: this.toPrice(order.shipping),
						TaxTotal     : this.toPrice(order.tax),
						PaymentMethod: order.payment_type_title || order.payment_type,
					},
				},
				ordered_products: items.map(item => ({
					event_name: "Ordered Product",
					unique_id : "pmw_" + orderId + "_" + item.ItemId,
					value     : item.RowTotal,
					properties: Object.assign({}, item, {
						$event_id: orderId + "_" + item.ItemId,
						$value   : item.RowTotal,
						OrderId  : String(order.number || order.id),
						Currency : currency,
					}),
				})),
			};
		}
	}

	// Register the adapter with capabilities.
	// Klaviyo's server-side events are sent from the WooCommerce order hooks,
	// not relayed from the browser, so no browser-side S2S is declared here.
	const adapter = new KlaviyoAdapter();
	pmw.registerAdapter("klaviyo", adapter, {
		mode                      : "browser_only",
		supportsS2S               : false,
		supportsEventDeduplication: false,
		isActive                  : () => pmwDataLayer?.pixels?.klaviyo?.public_api_key ? true : false,
	});

}(window.pmw = window.pmw || {}));
